import type {
  CategoryNavigationShopAll,
  CategoryNavigationTile,
} from '_components/CategoryNavigationTiles/types';
import { normalizePlpAssignmentPath } from './plpPathAssignment';

/**
 * Category Navigation Tiles content lives in a shared, reusable
 * `category_navigation` document (heading, SHOP ALL, tiles), so one set can
 * serve a CIP and all of its child PLPs. Pages link to it:
 *
 * - CIP / CLP / homepage: the `category_navigation_tiles` slice's
 *   `navigation` link (+ optional heading override)
 * - PLP: the collection's Category Navigation tab (`category_nav_document`,
 *   optional heading override, and placement)
 *
 * The linked document's fields arrive through `fetchLinks`
 * (`CATEGORY_NAVIGATION_FETCH_LINKS`), as `link.data`.
 */
export const CATEGORY_NAVIGATION_FETCH_LINKS = [
  'category_navigation.heading',
  'category_navigation.shop_all_url',
  'category_navigation.shop_all_label',
  'category_navigation.tiles',
];

export interface CategoryNavigationTileField {
  label?: string | null;
  url?: string | null;
  image?: { url?: string | null; alt?: string | null } | null;
}

/** The fields of a `category_navigation` document. */
export interface CategoryNavigationDocumentData {
  heading?: string | null;
  shop_all_url?: string | null;
  shop_all_label?: string | null;
  tiles?: CategoryNavigationTileField[] | null;
}

/** A content relationship to a `category_navigation` document. */
export interface CategoryNavigationLink {
  id?: string;
  type?: string;
  isBroken?: boolean;
  data?: CategoryNavigationDocumentData | null;
}

/**
 * The subset of a `category_navigation_tiles` slice this module relies on.
 * Embedded on `category_page`/`landing_page`/`homepage` as an inline slice, so
 * it has Prismic's `{ primary }` shape and no `data` wrapper.
 */
export interface CategoryNavigationTilesSlice {
  id?: string;
  slice_type?: string;
  primary?: {
    navigation?: CategoryNavigationLink | null;
    heading?: string | null;
  } | null;
}

export interface CategoryNavigationTilesData {
  heading: string;
  shopAll?: CategoryNavigationShopAll;
  items: CategoryNavigationTile[];
}

const trimmed = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null;

/**
 * Authors enter a relative path or an absolute URL, never a locale-prefixed
 * one. Same rule as `activityNavigationGrid`'s `buildHref`.
 */
const buildHref = (raw: string | null, locale?: string): string | null => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `/${locale ?? 'en'}${path}`;
};

/**
 * A tile href reduced to the same form as a PLP category path
 * (`camping-and-hiking/camp-furniture`): no origin, locale segment, products
 * prefix, query, hash, or trailing slash.
 */
const toCategoryPath = (href: string): string => {
  const pathname = href
    .replace(/^https?:\/\/[^/]+/i, '')
    .split(/[?#]/)[0]
    .replace(/^\/(en|fr)(?=\/|$)/i, '');

  return normalizePlpAssignmentPath(pathname);
};

/**
 * Maps a linked `category_navigation` document to `CategoryNavigationTiles`
 * props.
 *
 * - A broken or unresolved link renders nothing.
 * - `headingOverride` (set on the page) wins over the document's heading.
 * - A tile missing a label, a resolvable url, or an image is dropped rather
 *   than rendered broken.
 * - `excludeCategoryPath` drops the tile pointing at the current PLP, so a set
 *   shared by sibling categories never links a page to itself.
 * - SHOP ALL appears only when a url is authored. The label is optional and
 *   falls back to the component's localized "Shop all".
 * - Returns `null` when there is no heading or no tile survives, so the page
 *   never renders an empty section.
 */
export const transformCategoryNavigation = (
  link: CategoryNavigationLink | null | undefined,
  {
    locale,
    headingOverride,
    excludeCategoryPath,
  }: {
    locale?: string;
    headingOverride?: string | null;
    excludeCategoryPath?: string | null;
  } = {}
): CategoryNavigationTilesData | null => {
  const data = link && !link.isBroken ? link.data : null;
  if (!data) {
    return null;
  }

  const heading = trimmed(headingOverride) ?? trimmed(data.heading);
  if (!heading) {
    return null;
  }

  const excluded = excludeCategoryPath
    ? normalizePlpAssignmentPath(excludeCategoryPath.split('?')[0])
    : null;

  const items: CategoryNavigationTile[] = (data.tiles ?? []).flatMap((tile) => {
    const label = trimmed(tile?.label);
    const href = buildHref(trimmed(tile?.url), locale);
    const imageUrl = trimmed(tile?.image?.url);

    if (!label || !href || !imageUrl) {
      return [];
    }

    if (excluded && toCategoryPath(href) === excluded) {
      return [];
    }

    return [
      {
        label,
        href,
        image: { url: imageUrl, alt: trimmed(tile?.image?.alt) ?? '' },
      },
    ];
  });

  if (items.length === 0) {
    return null;
  }

  const shopAllHref = buildHref(trimmed(data.shop_all_url), locale);
  const shopAllLabel = trimmed(data.shop_all_label);

  return {
    heading,
    ...(shopAllHref && {
      shopAll: {
        href: shopAllHref,
        ...(shopAllLabel && { label: shopAllLabel }),
      },
    }),
    items,
  };
};

/** CIP / CLP / homepage slice → component props. */
export const transformCategoryNavigationTiles = (
  slice: CategoryNavigationTilesSlice | null | undefined,
  locale?: string
): CategoryNavigationTilesData | null =>
  transformCategoryNavigation(slice?.primary?.navigation, {
    locale,
    headingOverride: slice?.primary?.heading,
  });

/**
 * The "Category Navigation" tab of a PLP's `collection` document, read
 * structurally because the generated document type is wider than this module
 * needs.
 */
export interface CollectionCategoryNavigationFields {
  /**
   * The generated `ContentRelationshipField` type doesn't model `fetchLinks`
   * data, so the link is narrowed to `CategoryNavigationLink` below.
   */
  category_nav_document?: unknown;
  category_nav_heading?: string | null;
  category_nav_placement?: string | null;
}

/**
 * Where merch places the PLP section: in the header slot above the results
 * (where the legacy "Shop by category" carousel was), above the results but
 * after the deals strips, or below the results.
 */
export type PlpCategoryNavigationPlacement =
  | 'aboveResults'
  | 'aboveResultsAfterDeals'
  | 'belowResults';

export interface PlpCategoryNavigationData extends CategoryNavigationTilesData {
  placement: PlpCategoryNavigationPlacement;
}

/** The Select option that puts the section above the results. */
export const ABOVE_RESULTS_OPTION = 'above results';

/** The Select option that puts the section above the results, after deals. */
export const ABOVE_RESULTS_AFTER_DEALS_OPTION = 'above results, after deals';

const PLACEMENT_BY_OPTION: Record<string, PlpCategoryNavigationPlacement> = {
  [ABOVE_RESULTS_OPTION]: 'aboveResults',
  [ABOVE_RESULTS_AFTER_DEALS_OPTION]: 'aboveResultsAfterDeals',
};

/**
 * PLP category navigation from the collection's Category Navigation tab. The
 * tile for the PLP itself is dropped. Anything but an explicit "above results"
 * option keeps the default placement, below the results.
 */
export const transformCollectionCategoryNavigation = (
  data: CollectionCategoryNavigationFields | null | undefined,
  locale: string | undefined,
  categoryPath: string
): PlpCategoryNavigationData | null => {
  if (!data) return null;

  const tiles = transformCategoryNavigation(
    data.category_nav_document as CategoryNavigationLink | null | undefined,
    {
      locale,
      headingOverride: data.category_nav_heading,
      excludeCategoryPath: categoryPath,
    }
  );

  if (!tiles) return null;

  return {
    ...tiles,
    placement:
      PLACEMENT_BY_OPTION[trimmed(data.category_nav_placement) ?? ''] ??
      'belowResults',
  };
};

export default transformCategoryNavigationTiles;
